import type {
    Bi,
    Cell,
    Gender,
    NounTable,
    ReviewStatus,
    Vacana,
    Vibhakti
} from '@/sanskrit/lib/sanskrit/types'
import type { SourceId } from '@/sanskrit/lib/sanskrit/sources'

import { VACANAS, VIBHAKTIS } from '@/sanskrit/lib/sanskrit/types'

export type StemClass =
    | 'a-m'
    | 'a-n'
    | 'aa-f'
    | 'i-m'
    | 'i-f'
    | 'ii-f'
    | 'u-m'
    | 'u-f'
    | 'uu-f'
    | 'r-m'
    | 'r-f'
    | 'as-n'

export interface StemClassInfo {
    id: StemClass
    sa: string
    name: Bi
    gender: Gender
    model: string
    /** final letter removed from the stem before endings are attached */
    strip: string
}

export const STEM_CLASSES: Record<StemClass, StemClassInfo> = {
    'a-m': { id: 'a-m', sa: 'अकारान्त पुंलिङ्ग', name: { hi: 'अ से अन्त होने वाले पुंलिङ्ग शब्द', en: 'Masculine nouns in -a' }, gender: 'm', model: 'राम', strip: '' },
    'a-n': { id: 'a-n', sa: 'अकारान्त नपुंसकलिङ्ग', name: { hi: 'अ से अन्त होने वाले नपुंसकलिङ्ग शब्द', en: 'Neuter nouns in -a' }, gender: 'n', model: 'फल', strip: '' },
    'aa-f': { id: 'aa-f', sa: 'आकारान्त स्त्रीलिङ्ग', name: { hi: 'आ से अन्त होने वाले स्त्रीलिङ्ग शब्द', en: 'Feminine nouns in -ā' }, gender: 'f', model: 'लता', strip: 'ा' },
    'i-m': { id: 'i-m', sa: 'इकारान्त पुंलिङ्ग', name: { hi: 'इ से अन्त होने वाले पुंलिङ्ग शब्द', en: 'Masculine nouns in -i' }, gender: 'm', model: 'मुनि', strip: 'ि' },
    'i-f': { id: 'i-f', sa: 'इकारान्त स्त्रीलिङ्ग', name: { hi: 'इ से अन्त होने वाले स्त्रीलिङ्ग शब्द', en: 'Feminine nouns in -i' }, gender: 'f', model: 'मति', strip: 'ि' },
    'ii-f': { id: 'ii-f', sa: 'ईकारान्त स्त्रीलिङ्ग', name: { hi: 'ई से अन्त होने वाले स्त्रीलिङ्ग शब्द', en: 'Feminine nouns in -ī' }, gender: 'f', model: 'नदी', strip: 'ी' },
    'u-m': { id: 'u-m', sa: 'उकारान्त पुंलिङ्ग', name: { hi: 'उ से अन्त होने वाले पुंलिङ्ग शब्द', en: 'Masculine nouns in -u' }, gender: 'm', model: 'गुरु', strip: 'ु' },
    'u-f': { id: 'u-f', sa: 'उकारान्त स्त्रीलिङ्ग', name: { hi: 'उ से अन्त होने वाले स्त्रीलिङ्ग शब्द', en: 'Feminine nouns in -u' }, gender: 'f', model: 'धेनु', strip: 'ु' },
    'uu-f': { id: 'uu-f', sa: 'ऊकारान्त स्त्रीलिङ्ग', name: { hi: 'ऊ से अन्त होने वाले स्त्रीलिङ्ग शब्द', en: 'Feminine nouns in -ū' }, gender: 'f', model: 'वधू', strip: 'ू' },
    'r-m': { id: 'r-m', sa: 'ऋकारान्त पुंलिङ्ग', name: { hi: 'ऋ से अन्त होने वाले पुंलिङ्ग (सम्बन्धवाचक) शब्द', en: 'Masculine kinship nouns in -ṛ' }, gender: 'm', model: 'पितृ', strip: 'ृ' },
    'r-f': { id: 'r-f', sa: 'ऋकारान्त स्त्रीलिङ्ग', name: { hi: 'ऋ से अन्त होने वाले स्त्रीलिङ्ग शब्द', en: 'Feminine kinship nouns in -ṛ' }, gender: 'f', model: 'मातृ', strip: 'ृ' },
    'as-n': { id: 'as-n', sa: 'सकारान्त नपुंसकलिङ्ग', name: { hi: 'अस् से अन्त होने वाले नपुंसकलिङ्ग शब्द', en: 'Neuter nouns in -as' }, gender: 'n', model: 'मनस्', strip: 'स्' }
}

/**
 * Endings per class, 8 vibhaktis x 3 numbers. Alternatives are separated by "/".
 * "N" marks a न that becomes ण when the णत्व rule applies.
 */
const ENDINGS: Record<StemClass, string[][]> = {
    'a-m': [
        ['ः', 'ौ', 'ाः'],
        ['म्', 'ौ', 'ान्'],
        ['ेN', 'ाभ्याम्', 'ैः'],
        ['ाय', 'ाभ्याम्', 'ेभ्यः'],
        ['ात्', 'ाभ्याम्', 'ेभ्यः'],
        ['स्य', 'योः', 'ाNाम्'],
        ['े', 'योः', 'ेषु'],
        ['', 'ौ', 'ाः']
    ],
    'a-n': [
        ['म्', 'े', 'ाNि'],
        ['म्', 'े', 'ाNि'],
        ['ेN', 'ाभ्याम्', 'ैः'],
        ['ाय', 'ाभ्याम्', 'ेभ्यः'],
        ['ात्', 'ाभ्याम्', 'ेभ्यः'],
        ['स्य', 'योः', 'ाNाम्'],
        ['े', 'योः', 'ेषु'],
        ['', 'े', 'ाNि']
    ],
    'aa-f': [
        ['ा', 'े', 'ाः'],
        ['ाम्', 'े', 'ाः'],
        ['या', 'ाभ्याम्', 'ाभिः'],
        ['ायै', 'ाभ्याम्', 'ाभ्यः'],
        ['ायाः', 'ाभ्याम्', 'ाभ्यः'],
        ['ायाः', 'योः', 'ाNाम्'],
        ['ायाम्', 'योः', 'ासु'],
        ['े', 'े', 'ाः']
    ],
    'i-m': [
        ['िः', 'ी', 'यः'],
        ['िम्', 'ी', 'ीन्'],
        ['िNा', 'िभ्याम्', 'िभिः'],
        ['ये', 'िभ्याम्', 'िभ्यः'],
        ['ेः', 'िभ्याम्', 'िभ्यः'],
        ['ेः', '्योः', 'ीNाम्'],
        ['ौ', '्योः', 'िषु'],
        ['े', 'ी', 'यः']
    ],
    'i-f': [
        ['िः', 'ी', 'यः'],
        ['िम्', 'ी', 'ीः'],
        ['्या', 'िभ्याम्', 'िभिः'],
        ['्यै/ये', 'िभ्याम्', 'िभ्यः'],
        ['्याः/ेः', 'िभ्याम्', 'िभ्यः'],
        ['्याः/ेः', '्योः', 'ीNाम्'],
        ['्याम्/ौ', '्योः', 'िषु'],
        ['े', 'ी', 'यः']
    ],
    'ii-f': [
        ['ी', '्यौ', '्यः'],
        ['ीम्', '्यौ', 'ीः'],
        ['्या', 'ीभ्याम्', 'ीभिः'],
        ['्यै', 'ीभ्याम्', 'ीभ्यः'],
        ['्याः', 'ीभ्याम्', 'ीभ्यः'],
        ['्याः', '्योः', 'ीNाम्'],
        ['्याम्', '्योः', 'ीषु'],
        ['ि', '्यौ', '्यः']
    ],
    'u-m': [
        ['ुः', 'ू', 'वः'],
        ['ुम्', 'ू', 'ून्'],
        ['ुNा', 'ुभ्याम्', 'ुभिः'],
        ['वे', 'ुभ्याम्', 'ुभ्यः'],
        ['ोः', 'ुभ्याम्', 'ुभ्यः'],
        ['ोः', '्वोः', 'ूNाम्'],
        ['ौ', '्वोः', 'ुषु'],
        ['ो', 'ू', 'वः']
    ],
    'u-f': [
        ['ुः', 'ू', 'वः'],
        ['ुम्', 'ू', 'ूः'],
        ['्वा', 'ुभ्याम्', 'ुभिः'],
        ['्वै/वे', 'ुभ्याम्', 'ुभ्यः'],
        ['्वाः/ोः', 'ुभ्याम्', 'ुभ्यः'],
        ['्वाः/ोः', '्वोः', 'ूNाम्'],
        ['्वाम्/ौ', '्वोः', 'ुषु'],
        ['ो', 'ू', 'वः']
    ],
    'uu-f': [
        ['ूः', '्वौ', '्वः'],
        ['ूम्', '्वौ', 'ूः'],
        ['्वा', 'ूभ्याम्', 'ूभिः'],
        ['्वै', 'ूभ्याम्', 'ूभ्यः'],
        ['्वाः', 'ूभ्याम्', 'ूभ्यः'],
        ['्वाः', '्वोः', 'ूNाम्'],
        ['्वाम्', '्वोः', 'ूषु'],
        ['ु', '्वौ', '्वः']
    ],
    'r-m': [
        ['ा', 'रौ', 'रः'],
        ['रम्', 'रौ', 'ॄन्'],
        ['्रा', 'ृभ्याम्', 'ृभिः'],
        ['्रे', 'ृभ्याम्', 'ृभ्यः'],
        ['ुः', 'ृभ्याम्', 'ृभ्यः'],
        ['ुः', '्रोः', 'ॄणाम्'],
        ['रि', '्रोः', 'ृषु'],
        ['ः', 'रौ', 'रः']
    ],
    'r-f': [
        ['ा', 'रौ', 'रः'],
        ['रम्', 'रौ', 'ॄः'],
        ['्रा', 'ृभ्याम्', 'ृभिः'],
        ['्रे', 'ृभ्याम्', 'ृभ्यः'],
        ['ुः', 'ृभ्याम्', 'ृभ्यः'],
        ['ुः', '्रोः', 'ॄणाम्'],
        ['रि', '्रोः', 'ृषु'],
        ['ः', 'रौ', 'रः']
    ],
    'as-n': [
        ['ः', 'सी', 'ांसि'],
        ['ः', 'सी', 'ांसि'],
        ['सा', 'ोभ्याम्', 'ोभिः'],
        ['से', 'ोभ्याम्', 'ोभ्यः'],
        ['सः', 'ोभ्याम्', 'ोभ्यः'],
        ['सः', 'सोः', 'साम्'],
        ['सि', 'सोः', 'ःसु/स्सु'],
        ['ः', 'सी', 'ांसि']
    ]
}

const NATVA_TRIGGERS = new Set(['र', 'ष', 'ऋ', 'ॠ', 'ृ', 'ॄ'])

const NATVA_ALLOWED = new Set([
    ...'अआइईउऊएऐओऔ',
    'ा', 'ि', 'ी', 'ु', 'ू', 'े', 'ै', 'ो', 'ौ',
    '्',
    'क', 'ख', 'ग', 'घ', 'ङ',
    'प', 'फ', 'ब', 'भ', 'म',
    'य', 'व', 'ह'
])

/**
 * णत्व (रषाभ्यां नो णः समानपदे 8.4.1, अट्कुप्वाङ्नुम्व्यवायेऽपि 8.4.2):
 * a न becomes ण when र, ष or ऋ precedes it in the same word with only
 * vowels, ह, य, व, guttural or labial consonants in between.
 */
export const natvaApplies = (before: string): boolean => {
    for (let i = before.length - 1; i >= 0; i--) {
        const ch = before[i]
        if (NATVA_TRIGGERS.has(ch)) return true
        if (!NATVA_ALLOWED.has(ch)) return false
    }

    return false
}

const applyEnding = (base: string, ending: string): string => {
    const index = ending.indexOf('N')
    if (index < 0) return base + ending

    const before = base + ending.slice(0, index)
    const n = natvaApplies(before) ? 'ण' : 'न'

    return before + n + ending.slice(index + 1)
}

export const baseOf = (stem: string, cls: StemClass): string => {
    const strip = STEM_CLASSES[cls].strip
    if (!strip) return stem
    if (!stem.endsWith(strip)) throw new Error(`Stem ${stem} does not fit class ${cls}`)
    return stem.slice(0, stem.length - strip.length)
}

export const ntable = (fn: (vib: Vibhakti, v: Vacana) => Cell): NounTable => {
    const table = {} as NounTable

    for (const vib of VIBHAKTIS) {
        table[vib] = {} as Record<Vacana, Cell>
        for (const v of VACANAS) table[vib][v] = fn(vib, v)
    }

    return table
}

/** Generate the full 8 x 3 table for a stem of a known class. */
export const declineStem = (stem: string, cls: StemClass): NounTable => {
    const base = baseOf(stem, cls)
    const endings = ENDINGS[cls]

    return ntable((vib, v) => {
        const spec = endings[vib - 1][VACANAS.indexOf(v)]
        return spec.split('/').map(ending => applyEnding(base, ending))
    })
}

/** Ending (as written after the base) for a class and cell, natva unresolved. */
export const classEnding = (cls: StemClass, vib: Vibhakti, v: Vacana): string => ENDINGS[cls][vib - 1][VACANAS.indexOf(v)].replace(/N/g, 'न')

/** Compact 24-cell writer for stored tables ("—" means no form exists). */
export const ntbl = (...cells: string[]): NounTable => {
    if (cells.length !== 24) throw new Error('A noun table needs 24 cells')

    return ntable((vib, v) => {
        const raw = cells[(vib - 1) * 3 + VACANAS.indexOf(v)]
        return raw === '—' ? [] : raw.split('/')
    })
}

export interface NounParadigm {
    id: string
    stem: string
    lemma: string
    gender: Gender
    cls?: StemClass
    kind: 'noun' | 'pronoun' | 'numeral'
    meaning: Bi
    table: NounTable
    generated: boolean
    status: ReviewStatus
    sources: SourceId[]
    note?: Bi
    /** Pronouns of the 1st/2nd person carry their own person. */
    purusha?: 'uttama' | 'madhyama'
}

const PARADIGM_SOURCES: SourceId[] = ['lsk', 'kale', 'macdonell']

const generated = (id: string, stem: string, cls: StemClass, meaning: Bi, note?: Bi): NounParadigm => {
    const table = declineStem(stem, cls)

    return {
        id,
        stem,
        lemma: table[1].eka[0],
        gender: STEM_CLASSES[cls].gender,
        cls,
        kind: 'noun',
        meaning,
        table,
        generated: true,
        status: 'verified',
        sources: PARADIGM_SOURCES,
        note
    }
}

/** Model paradigms shown in the Shabdarupa lab, each the standard model of its class. */
export const MODEL_PARADIGMS: NounParadigm[] = [
    generated('rama', 'राम', 'a-m', { hi: 'राम', en: 'Rāma' }, {
        hi: 'र के कारण तृतीया एकवचन (रामेण) और षष्ठी बहुवचन (रामाणाम्) में न का ण हो जाता है (णत्व)। बालक जैसे शब्दों में नहीं: बालकेन।',
        en: 'Because of the र, the न of the instrumental singular (रामेण) and genitive plural (रामाणाम्) becomes ण (the णत्व rule). Compare बालकेन, where no र precedes.'
    }),
    generated('balaka', 'बालक', 'a-m', { hi: 'बालक, लड़का', en: 'boy' }),
    generated('phala', 'फल', 'a-n', { hi: 'फल', en: 'fruit' }, {
        hi: 'नपुंसकलिङ्ग में प्रथमा और द्वितीया के रूप एक जैसे होते हैं: फलम्, फले, फलानि।',
        en: 'In the neuter, the nominative and accusative are identical: फलम्, फले, फलानि.'
    }),
    generated('lata', 'लता', 'aa-f', { hi: 'लता, बेल', en: 'creeper, vine' }),
    generated('muni', 'मुनि', 'i-m', { hi: 'मुनि', en: 'sage' }),
    generated('hari', 'हरि', 'i-m', { hi: 'हरि (विष्णु)', en: 'Hari' }),
    generated('mati', 'मति', 'i-f', { hi: 'बुद्धि', en: 'mind, thought' }, {
        hi: 'चतुर्थी से सप्तमी एकवचन में दो-दो स्वीकृत रूप हैं (मत्यै / मतये)।',
        en: 'Dative to locative singular each have two accepted forms (मत्यै / मतये).'
    }),
    generated('nadi', 'नदी', 'ii-f', { hi: 'नदी', en: 'river' }),
    generated('guru', 'गुरु', 'u-m', { hi: 'गुरु', en: 'teacher' }),
    generated('dhenu', 'धेनु', 'u-f', { hi: 'गाय', en: 'cow' }),
    generated('vadhu', 'वधू', 'uu-f', { hi: 'वधू', en: 'bride' }),
    generated('pitr', 'पितृ', 'r-m', { hi: 'पिता', en: 'father' }, {
        hi: 'सम्बन्धवाचक ऋकारान्त शब्द (पितृ, भ्रातृ)। दातृ जैसे कर्तृवाचक शब्दों के रूप अलग हैं (दाता, दातारौ)।',
        en: 'Kinship nouns in -ṛ (पितृ, भ्रातृ). Agent nouns like दातृ decline differently (दाता, दातारौ).'
    }),
    generated('matr', 'मातृ', 'r-f', { hi: 'माता', en: 'mother' }),
    generated('manas', 'मनस्', 'as-n', { hi: 'मन', en: 'mind' }),
    {
        id: 'rajan',
        stem: 'राजन्',
        lemma: 'राजा',
        gender: 'm',
        kind: 'noun',
        meaning: { hi: 'राजा', en: 'king' },
        table: ntbl(
            'राजा', 'राजानौ', 'राजानः',
            'राजानम्', 'राजानौ', 'राज्ञः',
            'राज्ञा', 'राजभ्याम्', 'राजभिः',
            'राज्ञे', 'राजभ्याम्', 'राजभ्यः',
            'राज्ञः', 'राजभ्याम्', 'राजभ्यः',
            'राज्ञः', 'राज्ञोः', 'राज्ञाम्',
            'राज्ञि/राजनि', 'राज्ञोः', 'राजसु',
            'राजन्', 'राजानौ', 'राजानः'
        ),
        generated: false,
        status: 'verified',
        sources: PARADIGM_SOURCES,
        note: {
            hi: 'नकारान्त: सबल रूपों में राजान्-, निर्बल में राज्ञ्- (अ लुप्त, ज् + न् = ज्ञ्), पद-रूपों में राज-।',
            en: 'A consonant stem in -an: strong forms राजान्-, weak forms राज्ञ्- (the अ drops and ज्+न् = ज्ञ्), and राज- before consonant endings.'
        }
    },
    {
        id: 'naman',
        stem: 'नामन्',
        lemma: 'नाम',
        gender: 'n',
        kind: 'noun',
        meaning: { hi: 'नाम', en: 'name' },
        table: ntbl(
            'नाम', 'नाम्नी/नामनी', 'नामानि',
            'नाम', 'नाम्नी/नामनी', 'नामानि',
            'नाम्ना', 'नामभ्याम्', 'नामभिः',
            'नाम्ने', 'नामभ्याम्', 'नामभ्यः',
            'नाम्नः', 'नामभ्याम्', 'नामभ्यः',
            'नाम्नः', 'नाम्नोः', 'नाम्नाम्',
            'नाम्नि/नामनि', 'नाम्नोः', 'नामसु',
            'नाम/नामन्', 'नाम्नी/नामनी', 'नामानि'
        ),
        generated: false,
        status: 'verified',
        sources: PARADIGM_SOURCES,
        note: {
            hi: 'नकारान्त नपुंसकलिङ्ग: प्रथमा एकवचन "नाम" (न् लुप्त)। निर्बल रूपों में अ का लोप: नाम्ना, नाम्नः।',
            en: 'A neuter stem in -an: the nominative singular is नाम (the न् drops). Weak forms lose the अ: नाम्ना, नाम्नः.'
        }
    },
    {
        id: 'jagat',
        stem: 'जगत्',
        lemma: 'जगत्',
        gender: 'n',
        kind: 'noun',
        meaning: { hi: 'संसार', en: 'world' },
        table: ntbl(
            'जगत्/जगद्', 'जगती', 'जगन्ति',
            'जगत्/जगद्', 'जगती', 'जगन्ति',
            'जगता', 'जगद्भ्याम्', 'जगद्भिः',
            'जगते', 'जगद्भ्याम्', 'जगद्भ्यः',
            'जगतः', 'जगद्भ्याम्', 'जगद्भ्यः',
            'जगतः', 'जगतोः', 'जगताम्',
            'जगति', 'जगतोः', 'जगत्सु',
            'जगत्/जगद्', 'जगती', 'जगन्ति'
        ),
        generated: false,
        status: 'verified',
        sources: PARADIGM_SOURCES,
        note: {
            hi: 'तकारान्त नपुंसकलिङ्ग: भ्याम्, भिः, भ्यः से पहले त् का द् हो जाता है।',
            en: 'A neuter stem in -t: before भ्याम्, भिः, भ्यः the त् becomes द्.'
        }
    }
]

const PRONOUN_SOURCES: SourceId[] = ['lsk', 'kale', 'macdonell']

const tadM = ntbl(
    'सः', 'तौ', 'ते',
    'तम्', 'तौ', 'तान्',
    'तेन', 'ताभ्याम्', 'तैः',
    'तस्मै', 'ताभ्याम्', 'तेभ्यः',
    'तस्मात्', 'ताभ्याम्', 'तेभ्यः',
    'तस्य', 'तयोः', 'तेषाम्',
    'तस्मिन्', 'तयोः', 'तेषु',
    '—', '—', '—'
)

const tadF = ntbl(
    'सा', 'ते', 'ताः',
    'ताम्', 'ते', 'ताः',
    'तया', 'ताभ्याम्', 'ताभिः',
    'तस्यै', 'ताभ्याम्', 'ताभ्यः',
    'तस्याः', 'ताभ्याम्', 'ताभ्यः',
    'तस्याः', 'तयोः', 'तासाम्',
    'तस्याम्', 'तयोः', 'तासु',
    '—', '—', '—'
)

const withNomAcc = (base: NounTable, nom: Cell[], acc: Cell[], voc?: Cell[]): NounTable => {
    return ntable((vib, v) => {
        const i = VACANAS.indexOf(v)
        if (vib === 1) return nom[i]
        if (vib === 2) return acc[i]
        if (vib === 8 && voc) return voc[i]
        return base[vib][v]
    })
}

const etadM = ntbl(
    'एषः', 'एतौ', 'एते',
    'एतम्', 'एतौ', 'एतान्',
    'एतेन', 'एताभ्याम्', 'एतैः',
    'एतस्मै', 'एताभ्याम्', 'एतेभ्यः',
    'एतस्मात्', 'एताभ्याम्', 'एतेभ्यः',
    'एतस्य', 'एतयोः', 'एतेषाम्',
    'एतस्मिन्', 'एतयोः', 'एतेषु',
    '—', '—', '—'
)

const etadF = ntbl(
    'एषा', 'एते', 'एताः',
    'एताम्', 'एते', 'एताः',
    'एतया', 'एताभ्याम्', 'एताभिः',
    'एतस्यै', 'एताभ्याम्', 'एताभ्यः',
    'एतस्याः', 'एताभ्याम्', 'एताभ्यः',
    'एतस्याः', 'एतयोः', 'एतासाम्',
    'एतस्याम्', 'एतयोः', 'एतासु',
    '—', '—', '—'
)

const kimM = ntbl(
    'कः', 'कौ', 'के',
    'कम्', 'कौ', 'कान्',
    'केन', 'काभ्याम्', 'कैः',
    'कस्मै', 'काभ्याम्', 'केभ्यः',
    'कस्मात्', 'काभ्याम्', 'केभ्यः',
    'कस्य', 'कयोः', 'केषाम्',
    'कस्मिन्', 'कयोः', 'केषु',
    '—', '—', '—'
)

const kimF = ntbl(
    'का', 'के', 'काः',
    'काम्', 'के', 'काः',
    'कया', 'काभ्याम्', 'काभिः',
    'कस्यै', 'काभ्याम्', 'काभ्यः',
    'कस्याः', 'काभ्याम्', 'काभ्यः',
    'कस्याः', 'कयोः', 'कासाम्',
    'कस्याम्', 'कयोः', 'कासु',
    '—', '—', '—'
)

const sarvaM = ntbl(
    'सर्वः', 'सर्वौ', 'सर्वे',
    'सर्वम्', 'सर्वौ', 'सर्वान्',
    'सर्वेण', 'सर्वाभ्याम्', 'सर्वैः',
    'सर्वस्मै', 'सर्वाभ्याम्', 'सर्वेभ्यः',
    'सर्वस्मात्', 'सर्वाभ्याम्', 'सर्वेभ्यः',
    'सर्वस्य', 'सर्वयोः', 'सर्वेषाम्',
    'सर्वस्मिन्', 'सर्वयोः', 'सर्वेषु',
    'सर्व', 'सर्वौ', 'सर्वे'
)

const sarvaF = ntbl(
    'सर्वा', 'सर्वे', 'सर्वाः',
    'सर्वाम्', 'सर्वे', 'सर्वाः',
    'सर्वया', 'सर्वाभ्याम्', 'सर्वाभिः',
    'सर्वस्यै', 'सर्वाभ्याम्', 'सर्वाभ्यः',
    'सर्वस्याः', 'सर्वाभ्याम्', 'सर्वाभ्यः',
    'सर्वस्याः', 'सर्वयोः', 'सर्वासाम्',
    'सर्वस्याम्', 'सर्वयोः', 'सर्वासु',
    'सर्वे', 'सर्वे', 'सर्वाः'
)

const pronoun = (id: string, stem: string, gender: Gender, meaning: Bi, table: NounTable, note?: Bi, purusha?: 'uttama' | 'madhyama'): NounParadigm => ({
    id,
    stem,
    lemma: table[1].eka[0] ?? table[1].bahu[0],
    gender,
    kind: 'pronoun',
    meaning,
    table,
    generated: false,
    status: 'verified',
    sources: PRONOUN_SOURCES,
    note,
    purusha
})

export const PRONOUN_PARADIGMS: NounParadigm[] = [
    pronoun('asmad', 'अस्मद्', 'm', { hi: 'मैं / हम', en: 'I / we' }, ntbl(
        'अहम्', 'आवाम्', 'वयम्',
        'माम्/मा', 'आवाम्/नौ', 'अस्मान्/नः',
        'मया', 'आवाभ्याम्', 'अस्माभिः',
        'मह्यम्/मे', 'आवाभ्याम्/नौ', 'अस्मभ्यम्/नः',
        'मत्', 'आवाभ्याम्', 'अस्मत्',
        'मम/मे', 'आवयोः/नौ', 'अस्माकम्/नः',
        'मयि', 'आवयोः', 'अस्मासु',
        '—', '—', '—'
    ), {
        hi: 'तीनों लिङ्गों में एक जैसे रूप। मा, मे, नौ, नः छोटे (अनुदात्त) रूप हैं जो वाक्य के आरम्भ में नहीं आते। इसका सम्बोधन नहीं होता।',
        en: 'Same forms in all genders. मा, मे, नौ, नः are short enclitic forms that never begin a sentence. There is no vocative.'
    }, 'uttama'),
    pronoun('yusmad', 'युष्मद्', 'm', { hi: 'तुम / तुम सब', en: 'you' }, ntbl(
        'त्वम्', 'युवाम्', 'यूयम्',
        'त्वाम्/त्वा', 'युवाम्/वाम्', 'युष्मान्/वः',
        'त्वया', 'युवाभ्याम्', 'युष्माभिः',
        'तुभ्यम्/ते', 'युवाभ्याम्/वाम्', 'युष्मभ्यम्/वः',
        'त्वत्', 'युवाभ्याम्', 'युष्मत्',
        'तव/ते', 'युवयोः/वाम्', 'युष्माकम्/वः',
        'त्वयि', 'युवयोः', 'युष्मासु',
        '—', '—', '—'
    ), {
        hi: 'तीनों लिङ्गों में एक जैसे रूप। त्वा, ते, वाम्, वः छोटे रूप हैं जो वाक्य के आरम्भ में नहीं आते।',
        en: 'Same forms in all genders. त्वा, ते, वाम्, वः are short enclitic forms that never begin a sentence.'
    }, 'madhyama'),
    pronoun('tad-m', 'तद्', 'm', { hi: 'वह (पुरुष)', en: 'he, that' }, tadM),
    pronoun('tad-f', 'तद्', 'f', { hi: 'वह (स्त्री)', en: 'she, that' }, tadF),
    pronoun('tad-n', 'तद्', 'n', { hi: 'वह (वस्तु)', en: 'it, that' }, withNomAcc(tadM, [['तत्'], ['ते'], ['तानि']], [['तत्'], ['ते'], ['तानि']])),
    pronoun('etad-m', 'एतद्', 'm', { hi: 'यह (पुरुष)', en: 'this (masc.)' }, etadM),
    pronoun('etad-f', 'एतद्', 'f', { hi: 'यह (स्त्री)', en: 'this (fem.)' }, etadF),
    pronoun('etad-n', 'एतद्', 'n', { hi: 'यह (वस्तु)', en: 'this (neut.)' }, withNomAcc(etadM, [['एतत्'], ['एते'], ['एतानि']], [['एतत्'], ['एते'], ['एतानि']])),
    pronoun('kim-m', 'किम्', 'm', { hi: 'कौन (पुरुष)', en: 'who (masc.)' }, kimM),
    pronoun('kim-f', 'किम्', 'f', { hi: 'कौन (स्त्री)', en: 'who (fem.)' }, kimF),
    pronoun('kim-n', 'किम्', 'n', { hi: 'क्या', en: 'what' }, withNomAcc(kimM, [['किम्'], ['के'], ['कानि']], [['किम्'], ['के'], ['कानि']])),
    pronoun('sarva-m', 'सर्व', 'm', { hi: 'सब (पुरुष)', en: 'all (masc.)' }, sarvaM, {
        hi: 'सर्वनाम की तरह: चतुर्थी सर्वस्मै, पञ्चमी सर्वस्मात्, सप्तमी सर्वस्मिन्, प्रथमा बहुवचन सर्वे (राम की तरह नहीं)।',
        en: 'Declined like a pronoun: सर्वस्मै, सर्वस्मात्, सर्वस्मिन्, and nominative plural सर्वे (not like राम).'
    }),
    pronoun('sarva-f', 'सर्व', 'f', { hi: 'सब (स्त्री)', en: 'all (fem.)' }, sarvaF),
    pronoun('sarva-n', 'सर्व', 'n', { hi: 'सब (वस्तु)', en: 'all (neut.)' }, withNomAcc(sarvaM, [['सर्वम्'], ['सर्वे'], ['सर्वाणि']], [['सर्वम्'], ['सर्वे'], ['सर्वाणि']], [['सर्व'], ['सर्वे'], ['सर्वाणि']])),
    pronoun('bhavat-m', 'भवत्', 'm', { hi: 'आप (पुरुष)', en: 'you (polite, masc.)' }, ntbl(
        'भवान्', 'भवन्तौ', 'भवन्तः',
        'भवन्तम्', 'भवन्तौ', 'भवतः',
        'भवता', 'भवद्भ्याम्', 'भवद्भिः',
        'भवते', 'भवद्भ्याम्', 'भवद्भ्यः',
        'भवतः', 'भवद्भ्याम्', 'भवद्भ्यः',
        'भवतः', 'भवतोः', 'भवताम्',
        'भवति', 'भवतोः', 'भवत्सु',
        'भवन्', 'भवन्तौ', 'भवन्तः'
    ), {
        hi: 'आदरसूचक "आप"। अर्थ मध्यम पुरुष का है, पर क्रिया प्रथम पुरुष में आती है: भवान् पठति।',
        en: 'The polite "you". It means the person addressed, but takes a प्रथम पुरुष verb: भवान् पठति (you read).'
    }),
    {
        ...pronoun('bhavat-f', 'भवती', 'f', { hi: 'आप (स्त्री)', en: 'you (polite, fem.)' }, declineStem('भवती', 'ii-f')),
        note: { hi: 'नदी की तरह: भवती, भवत्यौ, भवत्यः। क्रिया प्रथम पुरुष में: भवती पठति।', en: 'Declined like नदी: भवती, भवत्यौ, भवत्यः. The verb is प्रथम पुरुष: भवती पठति.' }
    }
]

export const ALL_PARADIGMS: NounParadigm[] = [...MODEL_PARADIGMS, ...PRONOUN_PARADIGMS]

const PARADIGM_BY_ID = new Map(ALL_PARADIGMS.map(p => [p.id, p]))

export const getNounParadigm = (id: string): NounParadigm | undefined => PARADIGM_BY_ID.get(id)

export interface NumeralInfo {
    id: string
    value: number
    forms: Partial<Record<Gender, string>>
    counting: string
    iast: string
    note?: Bi
}

/** Nominative forms of the numerals 1-10 (1-4 change with gender). */
export const NUMERALS: NumeralInfo[] = [
    { id: 'eka', value: 1, forms: { m: 'एकः', f: 'एका', n: 'एकम्' }, counting: 'एकम्', iast: 'ekam', note: { hi: 'केवल एकवचन में।', en: 'Singular only.' } },
    { id: 'dvi', value: 2, forms: { m: 'द्वौ', f: 'द्वे', n: 'द्वे' }, counting: 'द्वे', iast: 'dve', note: { hi: 'केवल द्विवचन में।', en: 'Dual only.' } },
    { id: 'tri', value: 3, forms: { m: 'त्रयः', f: 'तिस्रः', n: 'त्रीणि' }, counting: 'त्रीणि', iast: 'trīṇi', note: { hi: 'केवल बहुवचन में।', en: 'Plural only.' } },
    { id: 'catur', value: 4, forms: { m: 'चत्वारः', f: 'चतस्रः', n: 'चत्वारि' }, counting: 'चत्वारि', iast: 'catvāri', note: { hi: 'केवल बहुवचन में।', en: 'Plural only.' } },
    { id: 'panca', value: 5, forms: { m: 'पञ्च', f: 'पञ्च', n: 'पञ्च' }, counting: 'पञ्च', iast: 'pañca', note: { hi: 'पाँच से आगे तीनों लिङ्गों में एक जैसे।', en: 'From five on, the same in all genders.' } },
    { id: 'sas', value: 6, forms: { m: 'षट्', f: 'षट्', n: 'षट्' }, counting: 'षट्', iast: 'ṣaṭ' },
    { id: 'sapta', value: 7, forms: { m: 'सप्त', f: 'सप्त', n: 'सप्त' }, counting: 'सप्त', iast: 'sapta' },
    { id: 'asta', value: 8, forms: { m: 'अष्ट', f: 'अष्ट', n: 'अष्ट' }, counting: 'अष्ट', iast: 'aṣṭa', note: { hi: 'अष्ट और अष्टौ दोनों स्वीकृत।', en: 'Both अष्ट and अष्टौ are accepted.' } },
    { id: 'nava', value: 9, forms: { m: 'नव', f: 'नव', n: 'नव' }, counting: 'नव', iast: 'nava' },
    { id: 'dasa', value: 10, forms: { m: 'दश', f: 'दश', n: 'दश' }, counting: 'दश', iast: 'daśa' }
]

/** Full case tables for the gendered numerals, used by the analyser. */
export const NUMERAL_TABLES: Array<{ id: string; gender: Gender; value: number; table: NounTable }> = [
    { id: 'eka', gender: 'm', value: 1, table: ntbl('एकः', '—', '—', 'एकम्', '—', '—', 'एकेन', '—', '—', 'एकस्मै', '—', '—', 'एकस्मात्', '—', '—', 'एकस्य', '—', '—', 'एकस्मिन्', '—', '—', '—', '—', '—') },
    { id: 'eka', gender: 'f', value: 1, table: ntbl('एका', '—', '—', 'एकाम्', '—', '—', 'एकया', '—', '—', 'एकस्यै', '—', '—', 'एकस्याः', '—', '—', 'एकस्याः', '—', '—', 'एकस्याम्', '—', '—', '—', '—', '—') },
    { id: 'eka', gender: 'n', value: 1, table: ntbl('एकम्', '—', '—', 'एकम्', '—', '—', 'एकेन', '—', '—', 'एकस्मै', '—', '—', 'एकस्मात्', '—', '—', 'एकस्य', '—', '—', 'एकस्मिन्', '—', '—', '—', '—', '—') },
    { id: 'dvi', gender: 'm', value: 2, table: ntbl('—', 'द्वौ', '—', '—', 'द्वौ', '—', '—', 'द्वाभ्याम्', '—', '—', 'द्वाभ्याम्', '—', '—', 'द्वाभ्याम्', '—', '—', 'द्वयोः', '—', '—', 'द्वयोः', '—', '—', '—', '—') },
    { id: 'dvi', gender: 'f', value: 2, table: ntbl('—', 'द्वे', '—', '—', 'द्वे', '—', '—', 'द्वाभ्याम्', '—', '—', 'द्वाभ्याम्', '—', '—', 'द्वाभ्याम्', '—', '—', 'द्वयोः', '—', '—', 'द्वयोः', '—', '—', '—', '—') },
    { id: 'tri', gender: 'm', value: 3, table: ntbl('—', '—', 'त्रयः', '—', '—', 'त्रीन्', '—', '—', 'त्रिभिः', '—', '—', 'त्रिभ्यः', '—', '—', 'त्रिभ्यः', '—', '—', 'त्रयाणाम्', '—', '—', 'त्रिषु', '—', '—', '—') },
    { id: 'tri', gender: 'f', value: 3, table: ntbl('—', '—', 'तिस्रः', '—', '—', 'तिस्रः', '—', '—', 'तिसृभिः', '—', '—', 'तिसृभ्यः', '—', '—', 'तिसृभ्यः', '—', '—', 'तिसृणाम्', '—', '—', 'तिसृषु', '—', '—', '—') },
    { id: 'tri', gender: 'n', value: 3, table: ntbl('—', '—', 'त्रीणि', '—', '—', 'त्रीणि', '—', '—', 'त्रिभिः', '—', '—', 'त्रिभ्यः', '—', '—', 'त्रिभ्यः', '—', '—', 'त्रयाणाम्', '—', '—', 'त्रिषु', '—', '—', '—') },
    { id: 'catur', gender: 'm', value: 4, table: ntbl('—', '—', 'चत्वारः', '—', '—', 'चतुरः', '—', '—', 'चतुर्भिः', '—', '—', 'चतुर्भ्यः', '—', '—', 'चतुर्भ्यः', '—', '—', 'चतुर्णाम्', '—', '—', 'चतुर्षु', '—', '—', '—') },
    { id: 'catur', gender: 'f', value: 4, table: ntbl('—', '—', 'चतस्रः', '—', '—', 'चतस्रः', '—', '—', 'चतसृभिः', '—', '—', 'चतसृभ्यः', '—', '—', 'चतसृभ्यः', '—', '—', 'चतसृणाम्', '—', '—', 'चतसृषु', '—', '—', '—') },
    { id: 'catur', gender: 'n', value: 4, table: ntbl('—', '—', 'चत्वारि', '—', '—', 'चत्वारि', '—', '—', 'चतुर्भिः', '—', '—', 'चतुर्भ्यः', '—', '—', 'चतुर्भ्यः', '—', '—', 'चतुर्णाम्', '—', '—', 'चतुर्षु', '—', '—', '—') }
]

/** Generate masculine, feminine and neuter tables for an adjective in -a (feminine in -ā). */
export const declineAdjective = (stem: string, feminine?: string): Record<Gender, NounTable> => ({
    m: declineStem(stem, 'a-m'),
    f: declineStem(feminine ?? stem + 'ा', 'aa-f'),
    n: declineStem(stem, 'a-n')
})
